import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
//COMPONENTS
import Board from "./components/Board";
import ImageSet from "./components/ImageSet";
import Timer from "./components/Timer";
import Loading from "./components/Loading";
import Modal from "./components/Modal";
import Button from "./components/Button";
import InfoIcon from "./components/icons/InfoIcon";
import IconButton from "./components/IconButton";
import HintIcon from "./components/icons/HintIcon";
import { CELL_TYPE, HINTS_COUNT, SAVING_INTERVAL, SESSION_STORAGE_KEY } from "./defaults";
import { changeCell, clearPuzzle, getTranslatedString } from "./helpers";
//HOOKS
import useTimer from "./hooks/useTimer";
import useSudoku from "./hooks/useSudoku";
import Tooltip from "./components/Tooltip";
import CheckIcon from "./components/icons/CheckIcon";
const App = () => {
    const { id, puzzle, setPuzzle, solution, images, loading, isSolved, setIsSolved, instructions, checkSudoku, isFilled, } = useSudoku();
    const { time, setTime, stopped, stopTimer, resetTimer, startTimer, getTime } = useTimer();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [hintsCount, setHintsCount] = useState(HINTS_COUNT);
    const [showModal, setShowModal] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState(false);
    const [hintSelected, setHintSelected] = useState(false);
    const [saveLock, setSaveLock] = useState(false);
    const initMemory = () => {
        updateFromMemory();
        setInterval(() => {
            setSaveLock(true);
        }, SAVING_INTERVAL);
    };
    const getFromMemory = () => {
        const sudokus = localStorage.getItem(SESSION_STORAGE_KEY);
        if (!sudokus)
            return {
                items: []
            };
        return JSON.parse(sudokus);
    };
    const updateFromMemory = () => {
        if (!id)
            return;
        let sudokus = getFromMemory();
        let items = sudokus.items;
        let foundIndex = items.findIndex(item => item.id === id);
        if (foundIndex === -1)
            return;
        setupFromMemory(items[foundIndex]);
    };
    const setupFromMemory = (fromMemory) => {
        const { state, time, selectedIndex, hintsCount } = fromMemory;
        setPuzzle(state);
        setTime(time);
        setSelectedIndex(selectedIndex);
        setHintsCount(hintsCount);
    };
    const saveToMemory = () => {
        if (!id)
            return;
        let sudokus = getFromMemory();
        let items = sudokus.items;
        let currentSudoku = {
            id: id,
            state: puzzle,
            time: time,
            selectedIndex: selectedIndex,
            hintsCount: hintsCount
        };
        items = items.filter(item => item.id !== currentSudoku.id);
        items.push(currentSudoku);
        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ items }));
    };
    useEffect(() => {
        if (saveLock) {
            saveToMemory();
            setSaveLock(false);
        }
    }, [saveLock]);
    const onCellClick = (cell, rowIdx, cellIdx) => {
        if (cell.type === CELL_TYPE.SET || (selectedIndex === 0 && !hintSelected) || cell.type === CELL_TYPE.HINT)
            return;
        startTimer();
        let newValue = 0;
        cell.showState = false;
        // If hint is selected then set value to solution
        if (hintSelected && hintsCount > 0) {
            newValue = solution[rowIdx][cellIdx];
            setHintsCount(hintsCount - 1);
        }
        // If cell has different value than selected - set it to selected value
        if (!hintSelected && cell.value !== selectedIndex) {
            newValue = selectedIndex;
        }
        // If cell has same value as selected - clear it
        if (!hintSelected && cell.value === selectedIndex) {
            newValue = 0;
        }
        let newPuzzle = changeCell(puzzle, {
            type: hintSelected ? CELL_TYPE.HINT : CELL_TYPE.DEFAULT,
            value: newValue,
            valid: newValue === 0 ? true : newValue === solution[rowIdx][cellIdx]
        }, rowIdx, cellIdx);
        setPuzzle(newPuzzle);
    };
    const clear = () => {
        let cleared = clearPuzzle(puzzle);
        setPuzzle(cleared);
        resetTimer();
        setHintsCount(HINTS_COUNT);
        setIsSolved(false);
        setShowModal(false);
    };
    useEffect(() => {
        if (isSolved) {
            stopTimer();
            setShowModal(true);
        }
    }, [isSolved]);
    const onHintClick = () => {
        if (hintsCount > 0) {
            setHintSelected(true);
            setSelectedIndex(0);
        }
    };
    const onCheckClick = () => {
        setHintSelected(false);
        checkSudoku();
    };
    useEffect(() => {
        if (hintsCount === 0) {
            setHintSelected(false);
        }
    }, [hintsCount]);
    useEffect(() => {
        if (selectedIndex !== 0) {
            setHintSelected(false);
        }
    }, [selectedIndex]);
    useEffect(() => {
        const sudokuPageEl = document.querySelector('.js-sudoku-page');
        if (loading) {
            sudokuPageEl?.classList.add('is-loading');
            sudokuPageEl?.classList.remove('is-loaded');
        }
        else {
            sudokuPageEl?.classList.add('is-loaded');
            sudokuPageEl?.classList.remove('is-loading');
            initMemory();
        }
    }, [loading]);
    const getInfoModal = () => {
        return (_jsx(Modal, { title: getTranslatedString('instructions'), isOpen: showInfoModal, setIsOpen: setShowInfoModal, closeable: true, children: _jsx("div", { dangerouslySetInnerHTML: { __html: instructions } }) }));
    };
    const getSolvedModal = () => {
        return (_jsxs(Modal, { title: getTranslatedString('gameEnded'), isOpen: showModal, setIsOpen: setShowModal, children: [_jsxs("p", { children: [_jsx("strong", { children: getTranslatedString('time') }), " ", getTime()] }), _jsxs("p", { children: [_jsx("strong", { children: getTranslatedString('hintsLeft') }), " ", hintsCount] }), _jsxs("div", { className: "sudoku-controls__button-group", children: [_jsx(Button, { onClick: () => clear(), children: getTranslatedString('playAgain') }), _jsx(Button, { onClick: () => setShowModal(false), children: getTranslatedString('exit') })] })] }));
    };
    if (loading) {
        return _jsx("div", { className: "sudoku-game is-loading", children: _jsx(Loading, {}) });
    }
    return (_jsxs("div", { className: "sudoku-game is-loaded", children: [_jsx("div", { className: "sudoku-game__board", children: _jsx(Board, { puzzle: puzzle, solution: solution, images: images, onCellClick: onCellClick }) }), _jsxs("div", { className: "sudoku-game__controls", children: [_jsx(ImageSet, { images: images, selectedIndex: selectedIndex, setSelectedIndex: setSelectedIndex }), _jsx(Button, { className: "sudoku-controls__clear", onClick: clear, children: getTranslatedString('clear') }), _jsxs("div", { className: "sudoku-controls__actions", children: [_jsx(Timer, { time: time, setTime: setTime, getTime: getTime, stopped: stopped }), _jsx(IconButton, { onClick: () => setShowInfoModal(true), children: _jsx(InfoIcon, {}) }), _jsx(Tooltip, { label: hintSelected ? getTranslatedString('hintTooltip') : '', children: _jsx(IconButton, { onClick: () => onHintClick(), disabled: hintsCount === 0, className: hintSelected ? 'sudoku-controls__icon-button--hints is-active' : 'sudoku-controls__icon-button--hints', children: _jsx(HintIcon, { count: hintsCount }) }) }), _jsx(Tooltip, { label: getTranslatedString('checkTooltip'), children: _jsx(IconButton, { onClick: () => onCheckClick(), disabled: !isFilled, children: _jsx(CheckIcon, {}) }) })] })] }), getSolvedModal(), getInfoModal()] }));
};
export default App;
